import React, { useEffect, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDispatch, useSelector } from 'react-redux'
import constants from '../../../../js/lib/constants'
import { postData } from '../../../../js/helpers/apiHelper'
import {
  setSelectedVehicle,
  setModifiedPrices,
  setModifiedValues,
  setNeedsChanges,
  setShowAddressFields,
  setShowMilesNBouns,
  setStep,
  setType,
  setCancelationFee,
  resetStep3,
  setSpecialRequests,
} from '../../../../js/lib/redux/slices/booking'
import ReservationFields from './ReservationFields'
import DriverFields from './DriverFields'
import FlightDetails from '../../Booking/components/FlightDetails'
import ExtrasFields from './ExtrasFields'
import CommentsSection from '../../Booking/components/CommentsSection'
import SummaryMobilePanel from '../../Booking/components/SummaryMobilePanel'
import * as lib from '../../../../js/lib/lib'
import Alert from '../../Base/Alert'

const ModifyForm = () => {
  const {
    dictionary,
    validation,
    values,
    airlineOptions,
    configuration,
    reservationDetails,
    vehicle,
    comments,
  } = window.modifyData

  const { pendingPayment, showMilesNBonus, initialRedeemedMiles } = values || {}

  const { selectedExtras, isPrepaid, specialRequests } = useSelector(
    (state) => state.booking
  )

  const { estimatedTotal } = isPrepaid
    ? vehicle?.car?.prepaidPrice
    : vehicle?.car?.returnPrice
  const [alertMessages, setAlertMessages] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // console.log('extras', selectedExtras)

  const dispatch = useDispatch()

  useEffect(() => {
    // Initialize comments from DOM data if specialRequests is empty (fresh entry)
    if (!specialRequests && comments) {
      dispatch(setSpecialRequests(comments))
    }
  }, [])

  const schema = z.object({
    pickUpLocation: z.object(
      {
        text: z.string(),
        value: z.string(),
        country: z.string(),
      },
      { invalid_type_error: validation?.required }
    ),
    dropOffLocation: z
      .object(
        {
          text: z.string(),
          value: z.string(),
          country: z.string(),
        },
        { invalid_type_error: validation?.required }
      )
      .optional(),
    pickUpDate: z.string(),
    pickUpTime: z.string(),
    dropOffDate: z.string(),
    dropOffTime: z.string(),
    //driver details
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    countryCode: z.string(),
    phone: z.string(),
    //flight details
    airline: z.object({
      value: z.string(),
      text: z.string(),
    }),
    flightNumber: z.string().optional(),
    comments: z
      .string()
      .max(250, 'Max characters 250')
      .optional()
      .or(z.literal('')),
  })
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      ...values,
      pickUpLocation: values.pickUpLocation
        ? {
            text: values.pickUpLocation.text,
            value: values.pickUpLocation.code,
            country: values.pickUpLocation.country,
          }
        : '',
      dropOffLocation: values.dropOffLocation
        ? {
            text: values.dropOffLocation.text,
            value: values.dropOffLocation.code,
            country: values.dropOffLocation.country,
          }
        : '',
      comments: specialRequests || comments || '',
    },
  })

  const handleError = (messages) => {
    setAlertMessages(
      messages.length > 0 ? messages : [dictionary.defaultErrorMessage]
    )

    setTimeout(() => {
      lib.scrollToError(110)
    }, 0)
  }

  const onSubmit = (data) => {
    dispatch(resetStep3())
    if (pendingPayment) {
      dispatch(setShowMilesNBouns(showMilesNBonus))
      dispatch(setModifiedValues(getValues()))
      dispatch(setStep(constants.modify.pageNumber.acceptPendingPayment))
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      return
    }

    setIsLoading(true)
    const body = {
      ...data,
      pickUpLocation: {
        code: data.pickUpLocation.value,
        country: data.pickUpLocation.country,
      },
      dropOffLocation: {
        code: data.dropOffLocation.value,
        country: data.dropOffLocation.country,
      },
      amenities: selectedExtras.map(({ code, quantity }) => ({
        Code: code,
        Quantity: quantity,
      })),
    }
    postData(configuration.endpointModify, body)
      .then((response) => {
        if (!response.hasError) {
          dispatch(setSelectedVehicle(response.data.vehicle))
          handleModifiedPrices(response)
          dispatch(
            setShowMilesNBouns(
              response.data.showMilesNBonus && response.data.destinationAllowed
            )
          )
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
          dispatch(setSpecialRequests(data.comments))
          dispatch(setModifiedValues(getValues()))
          dispatch(setStep(constants.modify.pageNumber.acceptModification))
        } else {
          handleError(response.errorMessages)
        }
      })
      .catch(() => {
        handleError([dictionary.defaultErrorMessage])
      })
      .finally(() => setIsLoading(false))
  }

  const handleModifiedPrices = (response) => {
    const { needsChanges, priceDifference, hasAddressData } = response.data
    dispatch(setNeedsChanges(needsChanges))
    const modifiedPrices = {
      initialPaymentWithCard: estimatedTotal - (initialRedeemedMiles || 0),
      initialPaymentWithMiles: initialRedeemedMiles || 0,
    }
    if (priceDifference > 0) {
      modifiedPrices.restToBePaidWithCard = priceDifference
    } else if (priceDifference < 0) {
      modifiedPrices.refund = -1 * priceDifference
    }
    dispatch(setModifiedPrices(modifiedPrices))
    dispatch(setShowAddressFields(!hasAddressData))
  }

  const handleCancel = (e) => {
    if (pendingPayment) {
      dispatch(setCancelationFee(0))
      dispatch(setModifiedValues(getValues()))
      dispatch(setType('cancel'))
      dispatch(setStep(constants.modify.pageNumber.acceptModification))
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      return
    }

    e.preventDefault()
    setIsLoading(true)
    //send cancel request
    postData(configuration.endpointCancel, {
      referenceNumber: reservationDetails.referenceNumber,
      lastName: reservationDetails.lastName,
    })
      .then((response) => {
        if (!response.hasError) {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
          dispatch(setCancelationFee(response.data?.cancelationFee || 0))
          dispatch(setModifiedValues(getValues()))
          dispatch(setType('cancel'))
          dispatch(setStep(constants.modify.pageNumber.acceptModification))
        } else {
          handleError(response.errorMessages)
        }
      })
      .catch(() => {
        handleError([dictionary.defaultErrorMessage])
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <>
      {alertMessages.length > 0 && <Alert messages={alertMessages} />}
      <form
        onSubmit={handleSubmit(onSubmit)}
        // style={{ display: !remoteUrl ? 'block' : 'none' }}
      >
        <ReservationFields
          control={control}
          setValue={setValue}
          watch={watch}
          errors={errors}
          currentValues={values}
          pendingPayment={pendingPayment}
        />
        <div className="s-booking-form__separator"></div>
        <DriverFields control={control} />
        <div className="s-booking-form__separator"></div>
        <FlightDetails
          control={control}
          errors={errors}
          setValue={setValue}
          trigger={trigger}
          pendingPayment={pendingPayment}
        />
        {!pendingPayment && (
          <>
            <div className="s-booking-form__separator"></div>
            <ExtrasFields />
            <div className="s-booking-form__separator"></div>
            <CommentsSection control={control} errors={errors} />
            <div className="s-modify-form__footer">
              <button
                className="btn btn-outline-primary py-2"
                type="button"
                onClick={handleCancel}
                disabled={isLoading}>
                {dictionary.cancel}
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isLoading}>
                {dictionary.saveChanges}
              </button>
            </div>
          </>
        )}
        {pendingPayment && (
          <>
            <div className="s-modify-form__footer">
              <button
                className="btn btn-outline-primary py-2"
                type="button"
                onClick={handleCancel}
                disabled={isLoading}>
                {dictionary.cancelPendingPayment}
              </button>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isLoading}>
                {dictionary.acceptPendingPayment}
              </button>
            </div>
          </>
        )}

        <SummaryMobilePanel />
      </form>
    </>
  )
}

export default ModifyForm
